import { ReactElement } from "react"

import { Blockquote } from "@wfp/ui"

import { useGetLastFetchDataQuery } from "../../../services/marketprices/dashboard"

const headers = [
  {
    name: "",
    key: "status",
    width: 20,
  },
  {
    name: "Country",
    key: "country",
    width: 200,
  },
  {
    name: "Mapping Schema",
    key: "mappingSchema",
    width: 200,
  },
  {
    name: "Last Fetch Date",
    key: "lastFetch",
    width: 200,
  },
  {
    name: "Reports Count",
    key: "totalReports",
    width: 150,
  },
  {
    name: "Records Count",
    key: "totalRecords",
    width: 150,
  },
]

const FetchOperations = (): ReactElement => {
  const { data, isFetching, isError, isSuccess } = useGetLastFetchDataQuery(undefined, {
    pollingInterval: 300000,
  })

  const getColorStatus = (status: string): string => {
    if (status === "Success") return "bg-green-400"
    if (status === "Warning") return "bg-yellow-400"
    if (status === "Error") return "bg-red-400"
    return "bg-gray-400"
  }

  if (isError)
    return (
      <Blockquote kind="warning" className="mx-10 text-sm mb-2 w-fit">
        Unable to load fetch operations
      </Blockquote>
    )

  if (isSuccess && !data?.length)
    return (
      <Blockquote kind="info" className="mx-10 text-sm mb-2 w-fit">
        No fetch operations available
      </Blockquote>
    )

  return isFetching ? (
    <div className="flex justify-center w-full p-4 flex-col gap-2">
      <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
      <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
      <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
      <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
    </div>
  ) : (
    <div className="relative mx-8 mb-2">
      <div className="overflow-auto max-h-[400px]">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {headers.map(header => (
                <th
                  key={header.key}
                  className="text-left text-sm font-bold sticky top-0 bg-white z-30 p-1 !pb-2"
                  style={{ width: header.width }}
                >
                  {header.name}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data?.map((item, index) => (
              <tr key={`${index}-${item.country}`} className="border-b border-gray-200 border-dotted">
                <td className="text-sm p-1 text-center align-middle">
                  <div
                    title={item.status}
                    aria-label={`Status: ${item.status}`}
                    className={`w-3 h-3 mx-auto rounded-full ${getColorStatus(item.status)}`}
                  />
                </td>
                <td className="text-sm p-1">{item.country}</td>
                <td className="text-sm p-1">{item.mappingSchema}</td>
                <td className="text-sm p-1">
                  {item.lastSubmissionTime
                    ? new Date(item.lastSubmissionTime).toLocaleDateString("en-GB", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      })
                    : "Not started"}
                </td>
                <td className="text-sm p-1">{item.totalReports}</td>
                <td className="text-sm p-1">{item.totalRecords}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="sticky bottom-0 w-full flex justify-end m-0 mt-1 bg-white">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 text-[var(--interactive-01)] text-xs">
            Live • Updates every 5 mins
          </span>
        </div>
      </div>
    </div>
  )
}

export default FetchOperations
